import type { AccountOption } from "@app/components/account-combobox";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";

// A rule row that drag-reorders via a grip handle (mouse + keyboard through dnd-kit's
// sensors). The up/down arrows inside remain as an explicit step control. divide-y on
// the parent draws the row separators.
function SortableRuleRow({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="flex items-start gap-1 hover:bg-gray-50/40"
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        title="Drag to reorder"
        className="px-1.5 pt-3.5 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing shrink-0 touch-none"
      >
        ⠿
      </button>
      <div className="flex-1 min-w-0 p-3 pl-1 space-y-2">{children}</div>
    </div>
  );
}
import type { PreloadedQuery } from "react-relay";

import type { rulesPage_Query } from "@app/queries/rulesPage_Query.graphql";
import type { rulesPage_ListQuery } from "@app/queries/rulesPage_ListQuery.graphql";
import type { rulesPage_ExportQuery } from "@app/queries/rulesPage_ExportQuery.graphql";
import type { rulesPage_DeltaQuery } from "@app/queries/rulesPage_DeltaQuery.graphql";
import type { rulesPage_MergePreviewQuery } from "@app/queries/rulesPage_MergePreviewQuery.graphql";
import type { rulesPage_UpdateRuleMutation } from "@app/queries/rulesPage_UpdateRuleMutation.graphql";
import type { rulesPage_DeleteRuleMutation } from "@app/queries/rulesPage_DeleteRuleMutation.graphql";
import type { rulesPage_RecaptureRuleMutation } from "@app/queries/rulesPage_RecaptureRuleMutation.graphql";
import type { rulesPage_ReorderRulesMutation } from "@app/queries/rulesPage_ReorderRulesMutation.graphql";
import type { rulesPage_MergeRulesMutation } from "@app/queries/rulesPage_MergeRulesMutation.graphql";
import type { rulesPage_RulesUndoMutation } from "@app/queries/rulesPage_RulesUndoMutation.graphql";
import type { rulesPage_RulesRedoMutation } from "@app/queries/rulesPage_RulesRedoMutation.graphql";
import type { rulesPage_PresignQuery } from "@app/queries/rulesPage_PresignQuery.graphql";
import type { rulesPage_AttributionMemoryImportStartMutation } from "@app/queries/rulesPage_AttributionMemoryImportStartMutation.graphql";
import type { rulesPage_ImportPollQuery } from "@app/queries/rulesPage_ImportPollQuery.graphql";

import RecaptureDialog from "./components/recapture-dialog";
import type { Delta } from "./components/recapture-dialog";
import EditRuleDialog from "./components/edit-rule-dialog";
import MergeRuleDialog from "./components/merge-rule-dialog";
import StakeholderTxnsDialog from "@app/pages/categorize/stakeholder-txns-dialog";

// Initial load: one page of rules + the reference data the filters and dialogs need.
// attributionMemoryExport is intentionally NOT here — it serializes the whole config
// and is fetched lazily only when the operator clicks Export. The rule field
// selection is duplicated in listQuery below (kept identical so the row sees one
// shape); a masked fragment would add more machinery than the repetition costs.
export const rulesQuery = graphql`
  query rulesPage_Query(
    $filter: RulesFilterInputObject
    $sort: String
    $limit: Int
    $offset: Int
  ) {
    rules(filter: $filter, sort: $sort, limit: $limit, offset: $offset) {
      totalCount
      targetTypeCounts {
        targetType
        count
      }
      rules {
        id
        regex
        matchKind
        direction
        source
        enabled
        caseSensitive
        priority
        targetType
        matchField
        needsReview
        targetClassification
        matchesCount
        ownedCount
        startDate
        endDate
        stakeholder {
          id
          name
        }
        destinationBankAccount {
          id
          name
          nickname
          last4
        }
        expenseNature {
          id
          name
        }
        accounts {
          id
          name
          nickname
          last4
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures {
      id
      name
    }
    pendingSuggestions {
      totalCount
    }
  }
`;

// Re-fetched on every filter / sort / page change (and after per-rule mutations).
// Just the rules page — the reference data above doesn't change while browsing.
const listQuery = graphql`
  query rulesPage_ListQuery(
    $filter: RulesFilterInputObject
    $sort: String
    $limit: Int
    $offset: Int
  ) {
    rules(filter: $filter, sort: $sort, limit: $limit, offset: $offset) {
      totalCount
      targetTypeCounts {
        targetType
        count
      }
      rules {
        id
        regex
        matchKind
        direction
        source
        enabled
        caseSensitive
        priority
        targetType
        matchField
        needsReview
        targetClassification
        matchesCount
        ownedCount
        startDate
        endDate
        stakeholder {
          id
          name
        }
        destinationBankAccount {
          id
          name
          nickname
          last4
        }
        expenseNature {
          id
          name
        }
        accounts {
          id
          name
          nickname
          last4
        }
      }
    }
  }
`;

// Lazily fetched on Export click so the (expensive) whole-config serialization never
// runs on a normal page load.
const exportQuery = graphql`
  query rulesPage_ExportQuery {
    attributionMemoryExport
  }
`;

// Fetched on demand to preview a proposed change before applying it.
const deltaQuery = graphql`
  query rulesPage_DeltaQuery(
    $ruleId: ID!
    $regex: String
    $matchKind: String
    $direction: String
    $caseSensitive: Boolean
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $accountIds: [ID!]
    $delete: Boolean
    $perspectiveId: ID
  ) {
    ruleDeltaPreview(
      ruleId: $ruleId
      regex: $regex
      matchKind: $matchKind
      direction: $direction
      caseSensitive: $caseSensitive
      startDate: $startDate
      endDate: $endDate
      accountIds: $accountIds
      delete: $delete
      perspectiveId: $perspectiveId
    ) {
      capturedCount
      droppedCount
      manualConflictCount
      truncated
      captured {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedRuleAssigned {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedManual {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

const updateRuleMutation = graphql`
  mutation rulesPage_UpdateRuleMutation($input: UpdateRuleInputObject!) {
    updateRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const deleteRuleMutation = graphql`
  mutation rulesPage_DeleteRuleMutation($input: DeleteRuleInputObject!) {
    deleteRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const recaptureRuleMutation = graphql`
  mutation rulesPage_RecaptureRuleMutation(
    $input: RecaptureRuleInputObject!
  ) {
    recaptureRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const reorderRulesMutation = graphql`
  mutation rulesPage_ReorderRulesMutation($input: ReorderRulesInputObject!) {
    reorderRules(input: $input) {
      rules {
        id
      }
      errors {
        message
      }
    }
  }
`;

// Config import goes through S3 like every other file import: presign, PUT the
// JSON to S3, then start an async worker and poll its phase. The old synchronous
// mutation ran the whole import + re-derive inline and froze the page.
const importPresignQuery = graphql`
  query rulesPage_PresignQuery($fileExtension: String!, $nonce: String!) {
    uploadPresign(fileExtension: $fileExtension, nonce: $nonce) {
      signedUrl
      storageKey
    }
  }
`;

const importStartMutation = graphql`
  mutation rulesPage_AttributionMemoryImportStartMutation(
    $multiUploadInput: MultiUploadInputObject!
  ) {
    attributionMemoryImportStart(multiUploadInput: $multiUploadInput) {
      multiUpload {
        id
        phase
      }
      errors {
        message
      }
    }
  }
`;

const importPollQuery = graphql`
  query rulesPage_ImportPollQuery($id: ID!) {
    multiUpload(id: $id) {
      phase
      state
      errorMessage
    }
  }
`;

// Server phases → a label + baseline bar fill. The client-side upload owns the
// first 0–25% (its real byte progress is scaled into that band, below) so the bar
// only ever moves forward; the server phases pick up from there.
const IMPORT_PHASES: Record<string, { label: string; pct: number }> = {
  QUEUED: { label: "Queued", pct: 30 },
  IMPORTING: { label: "Applying rules & categories", pct: 60 },
  DERIVING: { label: "Re-deriving", pct: 90 },
  DONE: { label: "Done", pct: 100 },
};
const UPLOAD_BAND = 25; // % of the bar the file upload occupies

// Previews the common-prefix covering pattern + delta when merging the selected
// rules (two or more) against their combined coverage.
const mergePreviewQuery = graphql`
  query rulesPage_MergePreviewQuery(
    $ruleIds: [ID!]!
    $regex: String
    $matchKind: String
    $perspectiveId: ID
  ) {
    ruleMergePreview(
      ruleIds: $ruleIds
      regex: $regex
      matchKind: $matchKind
      perspectiveId: $perspectiveId
    ) {
      coveringRegex
      coveringMatchKind
      capturedCount
      droppedCount
      manualConflictCount
      truncated
      captured {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedRuleAssigned {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
      droppedManual {
        txn {
          id
        }
        postingDate
        description
        amount
        accountNumber
        expenseNature {
          id
          name
        }
      }
    }
  }
`;

const mergeRulesMutation = graphql`
  mutation rulesPage_MergeRulesMutation($input: MergeRulesInputObject!) {
    mergeRules(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const rulesUndoMutation = graphql`
  mutation rulesPage_RulesUndoMutation($input: RulesUndoInputObject!) {
    rulesUndo(input: $input) {
      undoneKind
      errors {
        message
      }
    }
  }
`;

const rulesRedoMutation = graphql`
  mutation rulesPage_RulesRedoMutation($input: RulesRedoInputObject!) {
    rulesRedo(input: $input) {
      redoneKind
      errors {
        message
      }
    }
  }
`;

type RulesPayload = rulesPage_Query["response"]["rules"];
type Rule = RulesPayload["rules"][number];

// Smaller page = fewer per-rule match/owned COUNT scans per fetch → snappier paging.
export const PAGE_SIZE = 25;

// Tabs mirror the four independent target dimensions. "All" keeps the priority list
// whole; a tab narrows to one flavor (where precedence actually competes).
const TABS: { key: string | null; label: string }[] = [
  { key: null, label: "All" },
  { key: "STAKEHOLDER", label: "Stakeholder" },
  { key: "CLASSIFICATION", label: "Classification" },
  { key: "INTERNAL_LINK", label: "Internal link" },
  { key: "NATURE", label: "Nature" },
];
const SOURCES = ["MANUAL", "SUGGESTED", "LEGACY_SEED", "IMPORTED"];
const MATCH_KINDS = ["REGEX", "PREFIX", "EXACT", "SUBSTRING"];
const DIRECTIONS = ["ANY", "OUTFLOW", "INFLOW"];
const SORTS: { value: string; label: string }[] = [
  { value: "PRIORITY", label: "Priority" },
  { value: "MATCHES_DESC", label: "Most matches" },
  { value: "MATCHES_ASC", label: "Fewest matches" },
  { value: "TARGET_ASC", label: "Target A–Z" },
  { value: "NEWEST", label: "Newest" },
];

// The shared confirm dialog handles delete + recapture (a no-attribute-change
// re-run). Rich attribute edits go through EditRuleDialog instead. `previewVars`
// drives the impact preview shown before confirming.
type DialogState = {
  rule: Rule;
  kind: "delete" | "recapture";
  title: string;
  confirmLabel: string;
  previewVars: {
    delete?: boolean;
  };
};

function targetLabel(rule: Rule): string {
  if (rule.targetType === "CLASSIFICATION")
    return rule.targetClassification ?? "—";
  if (rule.targetType === "INTERNAL_LINK")
    return rule.destinationBankAccount
      ? `${rule.destinationBankAccount.nickname || rule.destinationBankAccount.name}${
          rule.destinationBankAccount.last4
            ? ` ••${rule.destinationBankAccount.last4}`
            : ""
        }`
      : "—";
  if (rule.targetType === "NATURE") return rule.expenseNature?.name ?? "—";
  return rule.stakeholder?.name ?? "—";
}

function filterSummary(rule: Rule): string {
  const parts: string[] = [];
  if (rule.startDate || rule.endDate)
    parts.push(`${rule.startDate ?? "…"} → ${rule.endDate ?? "…"}`);
  if (rule.accounts.length > 0)
    parts.push(
      `${rule.accounts.length} account${rule.accounts.length === 1 ? "" : "s"}`
    );
  return parts.length ? parts.join(" · ") : "—";
}

export default function RulesPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<rulesPage_Query>;
}) {
  const preloaded = usePreloadedQuery(rulesQuery, queryRef);

  // Client cache of already-fetched pages, keyed by the full variables. A revisited
  // page (Prev/Next back-and-forth, or the prefetched next page) serves instantly
  // with no spinner; cleared on any mutation/import so it never serves stale rows.
  const pageCacheRef = useRef<Map<string, RulesPayload>>(new Map());
  const cacheKey = (vars: rulesPage_ListQuery["variables"]) => JSON.stringify(vars);

  // Reference data (stakeholders / accounts / natures / suggestions) is loaded once;
  // the rules page is refetched on its own as filters change.
  const [refData, setRefData] = useState(() => ({
    allStakeholders: preloaded.allStakeholders,
    bankAccounts: preloaded.bankAccounts,
    expenseNatures: preloaded.expenseNatures,
    pendingSuggestions: preloaded.pendingSuggestions,
  }));
  const [list, setList] = useState<RulesPayload>(preloaded.rules);
  useEffect(() => {
    setRefData({
      allStakeholders: preloaded.allStakeholders,
      bankAccounts: preloaded.bankAccounts,
      expenseNatures: preloaded.expenseNatures,
      pendingSuggestions: preloaded.pendingSuggestions,
    });
    setList(preloaded.rules);
    // Fresh route load: reset the cache and seed it with page 0 (the default vars the
    // loader used) so a return to page 0 is instant.
    pageCacheRef.current.clear();
    pageCacheRef.current.set(
      cacheKey({ filter: null, sort: "PRIORITY", limit: PAGE_SIZE, offset: 0 }),
      preloaded.rules
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded]);

  // ---- filter / sort / pagination state ----------------------------------
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [targetType, setTargetType] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<"ALL" | "ENABLED" | "DISABLED">("ALL");
  const [matchKind, setMatchKind] = useState("");
  const [direction, setDirection] = useState("");
  const [zeroMatches, setZeroMatches] = useState(false);
  const [needsReview, setNeedsReview] = useState(false);
  const [sort, setSort] = useState("PRIORITY");
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  // In flight while a page/filter/sort change refetches the list — drives the dim +
  // spinner so a page jump feels instant but visibly "loading", not broken.
  const [isFetching, setIsFetching] = useState(false);

  // Reorder only makes sense against the priority order; any other sort disables the
  // drag handle + step arrows (the splice would fight the visual order otherwise).
  const reorderable = sort === "PRIORITY";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const buildFilter = (): rulesPage_ListQuery["variables"]["filter"] => {
    const filter: NonNullable<rulesPage_ListQuery["variables"]["filter"]> = {};
    if (debouncedSearch.trim()) filter.search = debouncedSearch.trim();
    if (targetType) filter.targetType = targetType;
    if (source) filter.source = source;
    if (enabledFilter !== "ALL") filter.enabled = enabledFilter === "ENABLED";
    if (matchKind) filter.matchKind = matchKind;
    if (direction) filter.direction = direction;
    if (zeroMatches) filter.zeroMatches = true;
    if (needsReview) filter.needsReview = true;
    return Object.keys(filter).length ? filter : null;
  };

  const listVars = (): rulesPage_ListQuery["variables"] => ({
    filter: buildFilter(),
    sort,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  // Warm the cache with the next page so a forward click is instant. Best-effort and
  // silent (no spinner); skipped past the end or when already cached.
  const prefetchNext = (vars: rulesPage_ListQuery["variables"], total: number) => {
    const nextOffset = (vars.offset ?? 0) + (vars.limit ?? PAGE_SIZE);
    if (nextOffset >= total) return;
    const nextVars = { ...vars, offset: nextOffset };
    if (pageCacheRef.current.has(cacheKey(nextVars))) return;
    fetchQuery<rulesPage_ListQuery>(environment, listQuery, nextVars, { fetchPolicy: "network-only" })
      .toPromise()
      .then((res) => {
        if (res?.rules) pageCacheRef.current.set(cacheKey(nextVars), res.rules);
      })
      .catch(() => {});
  };

  // Serve a page from cache instantly when we have it (no spinner); otherwise fetch
  // it, cache it, and warm the next page.
  const fetchPage = (vars: rulesPage_ListQuery["variables"]) => {
    const cached = pageCacheRef.current.get(cacheKey(vars));
    if (cached) {
      setList(cached);
      prefetchNext(vars, cached.totalCount);
      return;
    }
    setIsFetching(true);
    fetchQuery<rulesPage_ListQuery>(environment, listQuery, vars, { fetchPolicy: "network-only" })
      .toPromise()
      .then((res) => {
        if (!res?.rules) return;
        pageCacheRef.current.set(cacheKey(vars), res.rules);
        setList(res.rules);
        prefetchNext(vars, res.rules.totalCount);
      })
      .finally(() => setIsFetching(false));
  };

  // Refetch just the rules page (filters, pagination).
  const reloadList = () => fetchPage(listVars());

  // Refetch reference data + the list together (after a structural change like an
  // import that can add stakeholders / natures / accounts). Invalidates the page
  // cache first — the underlying rows changed.
  const reloadAll = () => {
    pageCacheRef.current.clear();
    setIsFetching(true);
    fetchQuery<rulesPage_Query>(environment, rulesQuery, listVars(), { fetchPolicy: "network-only" })
      .toPromise()
      .then((res) => {
        if (!res) return;
        setRefData({
          allStakeholders: res.allStakeholders,
          bankAccounts: res.bankAccounts,
          expenseNatures: res.expenseNatures,
          pendingSuggestions: res.pendingSuggestions,
        });
        pageCacheRef.current.set(cacheKey(listVars()), res.rules);
        setList(res.rules);
      })
      .finally(() => setIsFetching(false));
  };

  // Re-fetch the list whenever a filter / sort / page changes (skip the first render,
  // which already has the preloaded page).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, targetType, source, enabledFilter, matchKind, direction, zeroMatches, needsReview, sort, page]);

  // Warm page 2 right after the initial (preloaded) load so even the first Next is
  // instant — the preloaded page came from the loader, so nothing prefetched it.
  useEffect(() => {
    prefetchNext(
      { filter: null, sort: "PRIORITY", limit: PAGE_SIZE, offset: 0 },
      preloaded.rules.totalCount
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloaded]);

  // Any filter change resets to the first page.
  const resetPage = () => setPage(0);

  const rules = list.rules;
  const totalCount = list.totalCount;
  const tabCount = (key: string | null) => {
    if (key === null) return list.targetTypeCounts.reduce((sum, c) => sum + c.count, 0);
    return list.targetTypeCounts.find((c) => c.targetType === key)?.count ?? 0;
  };

  const allStakeholders = refData.allStakeholders.filter(
    (s) =>
      !s.name.startsWith("&") &&
      !s.name.toLowerCase().includes("root stakeholder") &&
      s.name !== "_root"
  );
  const accountOptions: AccountOption[] = refData.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const allNatures = refData.expenseNatures;

  // The rich edit dialog (one rule at a time) + its live impact preview.
  const [editing, setEditing] = useState<Rule | null>(null);
  const [editDelta, setEditDelta] = useState<Delta | null>(null);
  const [editPreviewing, setEditPreviewing] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // The delta-preview confirm dialog.
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [delta, setDelta] = useState<Delta | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrideManual, setOverrideManual] = useState(false);

  // Select-two-rows → merge (the discoverable merge entry point).
  const [selectedRuleIds, setSelectedRuleIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [merge, setMerge] = useState<{ ruleIds: string[] } | null>(null);
  // The merge dialog's editable pattern (seeded from the common-prefix suggestion)
  // + its live impact preview.
  const [mergePattern, setMergePattern] = useState("");
  const [mergeMatchKind, setMergeMatchKind] = useState("PREFIX");
  const [mergeDelta, setMergeDelta] = useState<Delta | null>(null);
  const [mergePreviewing, setMergePreviewing] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [redoing, setRedoing] = useState(false);
  // Config import progress (null = idle). Phase drives the progress bar; error
  // surfaces a failed import on the page instead of freezing silently.
  const [importPhase, setImportPhase] = useState<string | null>(null);
  const [importUploadPct, setImportUploadPct] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const importPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => () => {
    if (importPollRef.current) clearInterval(importPollRef.current);
  }, []);
  // Drill from a stakeholder rule into that counterparty's full transactions dialog.
  const [txnsDialogStakeholder, setTxnsDialogStakeholder] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // A per-rule mutation changed the underlying rows — drop the cache before refetch.
  const refresh = () => {
    pageCacheRef.current.clear();
    reloadList();
  };

  // Fetch the preview for whatever the dialog is proposing.
  const runPreview = (state: DialogState) => {
    setPreviewing(true);
    setDelta(null);
    fetchQuery<rulesPage_DeltaQuery>(environment, deltaQuery, {
      ruleId: state.rule.id,
      ...state.previewVars,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setPreviewing(false);
        if (res) setDelta(res.ruleDeltaPreview as Delta);
      });
  };

  const openDialog = (state: DialogState) => {
    setOverrideManual(false);
    setDialog(state);
    runPreview(state);
  };

  const closeDialog = () => {
    setDialog(null);
    setDelta(null);
    setSaving(false);
  };

  const confirmDialog = (overrideTxnIds: string[]) => {
    if (!dialog) return;
    setSaving(true);
    const onDone = () => {
      closeDialog();
      refresh();
    };
    const onError = (err: Error) => {
      setSaving(false);
      window.alert(err.message);
    };

    if (dialog.kind === "delete") {
      commitMutation<rulesPage_DeleteRuleMutation>(environment, {
        mutation: deleteRuleMutation,
        variables: { input: { ruleId: dialog.rule.id } },
        onCompleted: onDone,
        onError,
      });
    } else {
      commitMutation<rulesPage_RecaptureRuleMutation>(environment, {
        mutation: recaptureRuleMutation,
        variables: { input: { ruleId: dialog.rule.id, overrideTxnIds } },
        onCompleted: onDone,
        onError,
      });
    }
  };

  // ---- rich edit dialog --------------------------------------------------

  const openEdit = (rule: Rule) => {
    setEditing(rule);
    setEditDelta(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditDelta(null);
    setEditSaving(false);
  };

  // Live impact preview for the draft in the edit dialog (debounced there).
  const runEditPreview = (vars: {
    regex: string;
    matchKind: string;
    direction: string;
    caseSensitive: boolean;
    startDate: string | null;
    endDate: string | null;
    accountIds: string[];
  }) => {
    if (!editing) return;
    setEditPreviewing(true);
    fetchQuery<rulesPage_DeltaQuery>(environment, deltaQuery, {
      ruleId: editing.id,
      ...vars,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setEditPreviewing(false);
        if (res) setEditDelta(res.ruleDeltaPreview as Delta);
      });
  };

  const saveEdit = (input: Record<string, unknown>) => {
    if (!editing) return;
    setEditSaving(true);
    commitMutation<rulesPage_UpdateRuleMutation>(environment, {
      mutation: updateRuleMutation,
      // The dialog builds a decoupled input (always carrying ruleId); narrow it
      // to the generated input type at this boundary.
      variables: {
        input: input as rulesPage_UpdateRuleMutation["variables"]["input"],
      },
      onCompleted: (res) => {
        setEditSaving(false);
        if (res.updateRule?.errors.length) {
          window.alert(res.updateRule.errors[0].message);
          return;
        }
        closeEdit();
        refresh();
      },
      onError: (err) => {
        setEditSaving(false);
        window.alert(err.message);
      },
    });
  };

  // ---- per-row actions ---------------------------------------------------

  const toggleEnabled = (rule: Rule, enabled: boolean) => {
    // INTERNAL_LINK: disabling offers keep-vs-unlink. Others toggle directly.
    if (rule.targetType === "INTERNAL_LINK" && !enabled) {
      internalLinkChange(rule, { enabled: false });
      return;
    }
    commitMutation<rulesPage_UpdateRuleMutation>(environment, {
      mutation: updateRuleMutation,
      variables: { input: { ruleId: rule.id, enabled } },
      onCompleted: () => refresh(),
      onError: (err) => window.alert(err.message),
    });
  };

  const deleteRule = (rule: Rule) => {
    if (rule.targetType === "INTERNAL_LINK") {
      internalLinkChange(rule, { delete: true });
      return;
    }
    openDialog({
      rule,
      kind: "delete",
      title: "Delete this rule",
      confirmLabel: "Delete rule",
      previewVars: { delete: true },
    });
  };

  const recapture = (rule: Rule) => {
    openDialog({
      rule,
      kind: "recapture",
      title: "Recapture this rule",
      confirmLabel: "Recapture",
      // No change — preview the rule's current matches.
      previewVars: {},
    });
  };

  const commitOrder = (order: string[]) => {
    commitMutation<rulesPage_ReorderRulesMutation>(environment, {
      mutation: reorderRulesMutation,
      variables: { input: { orderedIds: order } },
      onCompleted: () => refresh(),
      onError: (err) => window.alert(err.message),
    });
  };

  // Keyboard / explicit step reorder (the up/down arrows) — within the visible page.
  const move = (index: number, deltaIdx: number) => {
    const target = index + deltaIdx;
    if (target < 0 || target >= rules.length) return;
    const order = rules.map((r) => r.id);
    [order[index], order[target]] = [order[target], order[index]];
    commitOrder(order);
  };

  // Drag reorder (the grip handle), via dnd-kit — within the visible page.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );
  const ruleIds = rules.map((r) => r.id);
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ruleIds.indexOf(String(active.id));
    const to = ruleIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    commitOrder(arrayMove(ruleIds, from, to));
  };

  // INTERNAL_LINK disable/delete: a lighter confirm with an affected-count
  // summary (from the preview) and keep-existing-links vs unlink-all.
  const [internalDialog, setInternalDialog] = useState<{
    rule: Rule;
    action: { enabled?: boolean; delete?: boolean };
    affected: number | null;
  } | null>(null);

  const internalLinkChange = (
    rule: Rule,
    action: { enabled?: boolean; delete?: boolean }
  ) => {
    setInternalDialog({ rule, action, affected: null });
    fetchQuery<rulesPage_DeltaQuery>(environment, deltaQuery, {
      ruleId: rule.id,
      delete: true,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        if (res)
          setInternalDialog((prev) =>
            prev && prev.rule.id === rule.id
              ? { ...prev, affected: res.ruleDeltaPreview.droppedCount }
              : prev
          );
      });
  };

  const confirmInternal = (unlink: boolean) => {
    if (!internalDialog) return;
    const { rule, action } = internalDialog;
    const onDone = () => {
      setInternalDialog(null);
      refresh();
    };
    const onError = (err: Error) => window.alert(err.message);
    if (action.delete) {
      // Deletion always unlinks.
      commitMutation<rulesPage_DeleteRuleMutation>(environment, {
        mutation: deleteRuleMutation,
        variables: { input: { ruleId: rule.id } },
        onCompleted: onDone,
        onError,
      });
    } else {
      // Disable; reattributeScope ALL_MATCHING unlinks existing, OWNED keeps.
      commitMutation<rulesPage_UpdateRuleMutation>(environment, {
        mutation: updateRuleMutation,
        variables: {
          input: {
            ruleId: rule.id,
            enabled: false,
            reattributeScope: unlink ? "ALL_MATCHING" : "OWNED",
          },
        },
        onCompleted: onDone,
        onError,
      });
    }
  };

  // ---- select-two-rows merge --------------------------------------------

  const toggleRuleSelected = (ruleId: string) => {
    setSelectedRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  // Open the merge dialog: seed the editable pattern from the common-prefix
  // suggestion across ALL selected rules and show its initial impact preview.
  const openMerge = () => {
    const ruleIds = [...selectedRuleIds];
    if (ruleIds.length < 2) return;
    setMerge({ ruleIds });
    setMergeDelta(null);
    setMergePattern("");
    setMergePreviewing(true);
    fetchQuery<rulesPage_MergePreviewQuery>(environment, mergePreviewQuery, {
      ruleIds,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setMergePreviewing(false);
        const preview = res?.ruleMergePreview;
        if (!preview) return;
        setMergePattern(preview.coveringRegex ?? "");
        setMergeMatchKind(preview.coveringMatchKind ?? "PREFIX");
        setMergeDelta(preview as unknown as Delta);
      });
  };

  // Re-preview as the operator edits the merged pattern (debounced in the dialog).
  const runMergePreview = (regex: string, matchKind: string) => {
    if (!merge) return;
    setMergePreviewing(true);
    fetchQuery<rulesPage_MergePreviewQuery>(environment, mergePreviewQuery, {
      ruleIds: merge.ruleIds,
      regex,
      matchKind,
      perspectiveId: getPerspectiveId() ?? undefined,
    })
      .toPromise()
      .then((res) => {
        setMergePreviewing(false);
        if (res?.ruleMergePreview) setMergeDelta(res.ruleMergePreview as unknown as Delta);
      });
  };

  const closeMerge = () => {
    setMerge(null);
    setMergeDelta(null);
    setMergePattern("");
  };

  const confirmMerge = (
    regex: string,
    matchKind: string,
    overrideTxnIds: string[],
    excludeTxnIds: string[]
  ) => {
    if (!merge) return;
    setMergeSaving(true);
    commitMutation<rulesPage_MergeRulesMutation>(environment, {
      mutation: mergeRulesMutation,
      variables: {
        input: {
          ruleIds: merge.ruleIds,
          regex,
          matchKind,
          overrideTxnIds,
          excludeTxnIds,
        },
      },
      onCompleted: (res) => {
        setMergeSaving(false);
        if (res.mergeRules?.errors.length) {
          window.alert(res.mergeRules.errors[0].message);
          return;
        }
        closeMerge();
        setSelectedRuleIds(new Set());
        refresh();
      },
      onError: (err) => {
        setMergeSaving(false);
        window.alert(err.message);
      },
    });
  };

  // ---- global undo -------------------------------------------------------

  const undo = () => {
    setUndoing(true);
    commitMutation<rulesPage_RulesUndoMutation>(environment, {
      mutation: rulesUndoMutation,
      variables: { input: { perspectiveId: getPerspectiveId() ?? undefined } },
      onCompleted: (res) => {
        setUndoing(false);
        if (res.rulesUndo?.errors.length) {
          window.alert(res.rulesUndo.errors[0].message);
          return;
        }
        if (!res.rulesUndo?.undoneKind) {
          window.alert("Nothing to undo.");
          return;
        }
        reloadAll();
      },
      onError: (err) => {
        setUndoing(false);
        window.alert(err.message);
      },
    });
  };

  const redo = () => {
    setRedoing(true);
    commitMutation<rulesPage_RulesRedoMutation>(environment, {
      mutation: rulesRedoMutation,
      variables: { input: { perspectiveId: getPerspectiveId() ?? undefined } },
      onCompleted: (res) => {
        setRedoing(false);
        if (res.rulesRedo?.errors.length) {
          window.alert(res.rulesRedo.errors[0].message);
          return;
        }
        if (!res.rulesRedo?.redoneKind) {
          window.alert("Nothing to redo.");
          return;
        }
        reloadAll();
      },
      onError: (err) => {
        setRedoing(false);
        window.alert(err.message);
      },
    });
  };

  // ---- import / export ---------------------------------------------------

  const exportMemory = () => {
    setExporting(true);
    fetchQuery<rulesPage_ExportQuery>(environment, exportQuery, {})
      .toPromise()
      .then((res) => {
        setExporting(false);
        const json = res?.attributionMemoryExport;
        if (!json) return;
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "qbo-sync-attribution.json";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setExporting(false));
  };

  const pollImport = (multiUploadId: string) => {
    importPollRef.current = setInterval(() => {
      fetchQuery<rulesPage_ImportPollQuery>(environment, importPollQuery, {
        id: multiUploadId,
      })
        .toPromise()
        .then((data) => {
          const mu = data?.multiUpload;
          if (!mu) return;
          if (mu.phase) setImportPhase(mu.phase);
          if (mu.phase === "DONE") {
            if (importPollRef.current) clearInterval(importPollRef.current);
            setImportPhase(null);
            reloadAll();
          } else if (mu.phase === "FAILED" || mu.state === "FAILED") {
            if (importPollRef.current) clearInterval(importPollRef.current);
            setImportPhase(null);
            setImportError(mu.errorMessage ?? "Import failed");
          }
        });
    }, 1000);
  };

  // Import the config the same way every other file is imported: presign, PUT the
  // JSON to S3, then start an async worker and poll its phase — so a large config
  // + full re-derive never blocks the request (which froze the page before).
  const importMemory = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImportError(null);
    setImportUploadPct(0);
    setImportPhase("UPLOADING");

    const presignVars = { fileExtension: "json", nonce: `${Date.now()}-${file.name}` };
    fetchQuery<rulesPage_PresignQuery>(environment, importPresignQuery, presignVars, {
      fetchPolicy: "network-only",
    })
      .toPromise()
      .then(async (data) => {
        const presign = data?.uploadPresign;
        if (!presign) throw new Error("Could not presign the upload");

        // XHR (not fetch) so upload.onprogress reports real byte progress.
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", presign.signedUrl);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setImportUploadPct(Math.round((e.loaded / e.total) * 100));
            }
          };
          xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300
              ? resolve()
              : reject(new Error(`Upload to storage failed (HTTP ${xhr.status})`));
          xhr.onerror = () => reject(new Error("Upload to storage failed"));
          xhr.send(file);
        });
        setImportUploadPct(100);

        const descriptor = {
          id: presign.storageKey,
          storage: "cache" as const,
          metadata: {
            size: file.size,
            filename: file.name,
            mimeType: "application/json",
          },
        };
        setImportPhase("QUEUED");
        commitMutation<rulesPage_AttributionMemoryImportStartMutation>(environment, {
          mutation: importStartMutation,
          variables: { multiUploadInput: { uploads: [descriptor] } },
          onCompleted: (response) => {
            const result = response.attributionMemoryImportStart;
            const id = result?.multiUpload?.id;
            if (id) {
              pollImport(id);
            } else {
              setImportPhase(null);
              setImportError(result?.errors?.[0]?.message ?? "Import failed");
            }
          },
          onError: (err) => {
            setImportPhase(null);
            setImportError(err.message);
          },
        });
      })
      .catch((err: Error) => {
        setImportPhase(null);
        setImportError(err.message);
      });
  };

  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;

  // Rendered above AND below the list. Page changes update instantly; a cached /
  // prefetched page swaps in with no spinner (see fetchPage).
  const renderPagination = () =>
    totalCount > PAGE_SIZE ? (
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          </span>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    ) : null;

  const selectClass =
    "rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-400";

  // The body of one rule row — reused by the draggable and the plain (non-reorderable)
  // list so the markup stays in one place.
  const rowBody = (rule: Rule, index: number) => (
    <>
      {/* Pattern line — full width so a long regex wraps, not squeezed */}
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={selectedRuleIds.has(rule.id)}
          onChange={() => toggleRuleSelected(rule.id)}
          aria-label="Select rule for merge"
          className="mt-1.5 shrink-0"
        />
        {reorderable && (
          <div className="flex flex-col leading-none text-gray-400 shrink-0 pt-0.5">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              className="disabled:opacity-20 hover:text-gray-700"
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={index === rules.length - 1}
              onClick={() => move(index, 1)}
              className="disabled:opacity-20 hover:text-gray-700"
              aria-label="Move down"
            >
              ↓
            </button>
          </div>
        )}
        <code className="flex-1 min-w-0 rounded bg-gray-50 px-2 py-1 font-mono text-xs break-all select-text">
          {rule.regex}
        </code>
        {rule.matchKind !== "REGEX" && (
          <span
            className="shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] uppercase text-emerald-700"
            title="Indexed operator match (faster than regex)"
          >
            {rule.matchKind}
          </span>
        )}
        {rule.matchField === "QBO_COUNTERPARTY" && (
          <span
            className="shrink-0 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] uppercase text-sky-700"
            title="Matches the QBO vendor name (not the description)"
          >
            vendor
          </span>
        )}
        {rule.needsReview && (
          <span
            className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] uppercase text-amber-700"
            title="Target is uncategorized in the read perspective — re-point it to the right stakeholder"
          >
            needs review
          </span>
        )}
        <label
          className="flex items-center gap-1 text-xs text-gray-500 shrink-0"
          title="Enabled"
        >
          <input
            type="checkbox"
            checked={rule.enabled}
            onChange={(e) => toggleEnabled(rule, e.target.checked)}
          />
          on
        </label>
        <div className="flex items-center gap-2 shrink-0 text-xs">
          <button
            type="button"
            onClick={() => openEdit(rule)}
            className="font-medium text-emerald-600 hover:text-emerald-800"
          >
            edit
          </button>
          <button
            type="button"
            onClick={() => recapture(rule)}
            className="text-gray-400 hover:text-gray-700"
          >
            recapture
          </button>
          <button
            type="button"
            onClick={() => deleteRule(rule)}
            className="text-red-300 hover:text-red-600"
          >
            delete
          </button>
        </div>
      </div>

      {/* Meta line — target · captures · filters · direction · source */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pl-8 text-xs text-gray-600">
        <span>
          <span className="text-[10px] uppercase text-gray-400 mr-1">
            {rule.targetType === "INTERNAL_LINK"
              ? "link"
              : rule.targetType === "CLASSIFICATION"
              ? "class"
              : rule.targetType === "NATURE"
              ? "nature"
              : "to"}
          </span>
          {targetLabel(rule)}
          {rule.targetType === "STAKEHOLDER" && rule.stakeholder && (
            <button
              type="button"
              onClick={() =>
                setTxnsDialogStakeholder(
                  rule.stakeholder
                    ? { id: rule.stakeholder.id, name: rule.stakeholder.name }
                    : null
                )
              }
              title="Open this counterparty's transactions dialog"
              className="ml-1 text-gray-300 hover:text-gray-600"
            >
              ⤢
            </button>
          )}
        </span>
        <span title="Transactions whose description currently matches this rule">
          <span className="text-[10px] uppercase text-gray-400 mr-1">
            captures
          </span>
          {rule.matchesCount.toLocaleString()} match
          {rule.matchesCount === 1 ? "" : "es"}
          {rule.targetType === "STAKEHOLDER" && (
            <span className="text-gray-400">
              {" "}
              · {rule.ownedCount.toLocaleString()} owned
            </span>
          )}
        </span>
        <span>
          <span className="text-[10px] uppercase text-gray-400 mr-1">dir</span>
          {rule.direction}
        </span>
        {filterSummary(rule) !== "—" && (
          <span>
            <span className="text-[10px] uppercase text-gray-400 mr-1">
              filter
            </span>
            {filterSummary(rule)}
          </span>
        )}
        <span className="text-gray-400">
          <span className="text-[10px] uppercase mr-1">src</span>
          {rule.source}
        </span>
      </div>
    </>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold">Rules</h1>
        <span className="text-sm text-gray-500">
          {totalCount.toLocaleString()} rule{totalCount === 1 ? "" : "s"}, highest precedence first
        </span>
        {isFetching && (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            role="status"
            aria-label="Loading rules"
          />
        )}
        <a
          href="/rules/similar"
          className="text-sm text-emerald-700 hover:underline"
        >
          Find similar →
        </a>
        {refData.pendingSuggestions.totalCount > 0 && (
          <a
            href="/review"
            title="AI-proposed categorizations awaiting review"
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            {refData.pendingSuggestions.totalCount.toLocaleString()} need review →
          </a>
        )}
        <div className="ml-auto flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={undo}
            disabled={undoing}
            title="Undo the last rule change (exclude, carve, merge, split, …)"
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-40"
          >
            {undoing ? "Undoing…" : "Undo"}
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={redoing}
            title="Redo the last undone rule change"
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-40"
          >
            {redoing ? "Redoing…" : "Redo"}
          </button>
          <button
            type="button"
            onClick={exportMemory}
            disabled={exporting}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-40"
          >
            {exporting ? "Exporting…" : "Export JSON"}
          </button>
          <label
            className={`px-3 py-1.5 rounded border ${
              importPhase
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-50 cursor-pointer"
            }`}
          >
            Import JSON
            <input
              type="file"
              accept="application/json,.json"
              onChange={importMemory}
              disabled={importPhase !== null}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {importPhase &&
        (() => {
          const uploading = importPhase === "UPLOADING";
          // Upload owns 0–UPLOAD_BAND%; server phases take their baseline fill.
          const pct = uploading
            ? Math.round((importUploadPct / 100) * UPLOAD_BAND)
            : (IMPORT_PHASES[importPhase] ?? IMPORT_PHASES.QUEUED).pct;
          const label = uploading
            ? `Uploading ${importUploadPct}%`
            : `${(IMPORT_PHASES[importPhase] ?? IMPORT_PHASES.QUEUED).label}…`;
          // Pulse while the server works (its duration is opaque) so the bar
          // visibly signals activity; the upload band shows exact bytes instead.
          const working = !uploading && importPhase !== "DONE";
          return (
            <div className="rounded border bg-white px-3 py-2">
              <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                <span>{label}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded bg-gray-100">
                <div
                  className={`h-full bg-gray-900 transition-all duration-500 ease-out ${
                    working ? "animate-pulse" : ""
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}

      {importError && (
        <div
          role="alert"
          className="flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          <span className="break-words">Import failed: {importError}</span>
          <button
            type="button"
            onClick={() => setImportError(null)}
            className="text-red-400 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Target-type tabs (precedence competes only within a flavor) */}
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200">
        {TABS.map((tab) => {
          const active = targetType === tab.key;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => {
                setTargetType(tab.key);
                resetPage();
              }}
              className={`flex items-center gap-1.5 rounded-t px-3 py-1.5 text-sm ${
                active
                  ? "border-b-2 border-emerald-600 font-medium text-emerald-700"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab.label}
              <span
                className={`rounded-full px-1.5 text-[10px] ${
                  active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                }`}
              >
                {tabCount(tab.key).toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + filters + sort */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          placeholder="Search regex, counterparty, classification…"
          className="min-w-56 flex-1 rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            resetPage();
          }}
          className={selectClass}
          title="Source"
        >
          <option value="">Any source</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s.toLowerCase().replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <select
          value={enabledFilter}
          onChange={(e) => {
            setEnabledFilter(e.target.value as "ALL" | "ENABLED" | "DISABLED");
            resetPage();
          }}
          className={selectClass}
          title="Enabled state"
        >
          <option value="ALL">On &amp; off</option>
          <option value="ENABLED">Enabled</option>
          <option value="DISABLED">Disabled</option>
        </select>
        <select
          value={matchKind}
          onChange={(e) => {
            setMatchKind(e.target.value);
            resetPage();
          }}
          className={selectClass}
          title="Match kind"
        >
          <option value="">Any match</option>
          {MATCH_KINDS.map((m) => (
            <option key={m} value={m}>
              {m.toLowerCase()}
            </option>
          ))}
        </select>
        <select
          value={direction}
          onChange={(e) => {
            setDirection(e.target.value);
            resetPage();
          }}
          className={selectClass}
          title="Direction"
        >
          <option value="">Any direction</option>
          {DIRECTIONS.map((d) => (
            <option key={d} value={d}>
              {d.toLowerCase()}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600" title="Only rules that match no transaction">
          <input
            type="checkbox"
            checked={zeroMatches}
            onChange={(e) => {
              setZeroMatches(e.target.checked);
              resetPage();
            }}
          />
          0 matches
        </label>
        <label className="flex items-center gap-1.5 rounded border border-amber-200 px-2 py-1 text-xs text-amber-700" title="Only vendor mappings whose target is uncategorized in the read perspective — the divergent tail to re-point">
          <input
            type="checkbox"
            checked={needsReview}
            onChange={(e) => {
              setNeedsReview(e.target.checked);
              resetPage();
            }}
          />
          needs review
        </label>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <span>Sort</span>
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              resetPage();
            }}
            className={selectClass}
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!reorderable && (
        <p className="text-xs text-gray-400">
          Sort by <span className="font-medium">Priority</span> to drag-reorder rules.
        </p>
      )}

      {selectedRuleIds.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
          <span>{selectedRuleIds.size} selected</span>
          <button
            type="button"
            disabled={selectedRuleIds.size < 2}
            onClick={openMerge}
            title={
              selectedRuleIds.size >= 2
                ? "Merge the selected rules into one"
                : "Select two or more rules to merge"
            }
            className="px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-30 text-xs"
          >
            Merge selected
          </button>
          <button
            type="button"
            onClick={() => setSelectedRuleIds(new Set())}
            className="text-xs underline hover:no-underline"
          >
            Clear
          </button>
        </div>
      )}

      {renderPagination()}

      <div
        className={`transition-opacity duration-200 ${
          isFetching ? "pointer-events-none opacity-40" : ""
        }`}
        aria-busy={isFetching}
      >
      {rules.length === 0 ? (
        <p className="text-sm text-gray-400 bg-white border rounded p-6">
          {totalCount === 0 && buildFilter() === null ? (
            <>
              No rules yet. Attribute or classify transactions on the{" "}
              <a href="/transactions" className="underline">
                transactions page
              </a>{" "}
              and accept the rule suggestion, or import a previously exported{" "}
              <code className="text-xs">qbo-sync-attribution.json</code>.
            </>
          ) : (
            "No rules match these filters."
          )}
        </p>
      ) : reorderable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ruleIds} strategy={verticalListSortingStrategy}>
            <div className="bg-white rounded border divide-y">
              {rules.map((rule, index) => (
                <SortableRuleRow key={rule.id} id={rule.id}>
                  {rowBody(rule, index)}
                </SortableRuleRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="bg-white rounded border divide-y">
          {rules.map((rule, index) => (
            <div key={rule.id} className="flex items-start gap-1 hover:bg-gray-50/40">
              <div className="flex-1 min-w-0 p-3 space-y-2">{rowBody(rule, index)}</div>
            </div>
          ))}
        </div>
      )}
      </div>

      {renderPagination()}

      {editing && (
        <EditRuleDialog
          rule={editing}
          stakeholders={allStakeholders}
          accounts={accountOptions}
          natures={allNatures}
          delta={editDelta}
          previewing={editPreviewing}
          saving={editSaving}
          onPreview={runEditPreview}
          onSave={saveEdit}
          onCancel={closeEdit}
        />
      )}

      {dialog && (
        <RecaptureDialog
          title={dialog.title}
          confirmLabel={dialog.confirmLabel}
          delta={delta}
          loading={previewing}
          saving={saving}
          overrideManual={overrideManual}
          onToggleOverride={setOverrideManual}
          onConfirm={confirmDialog}
          onCancel={closeDialog}
        />
      )}

      {merge && (
        <MergeRuleDialog
          initialPattern={mergePattern}
          initialMatchKind={mergeMatchKind}
          delta={mergeDelta}
          previewing={mergePreviewing}
          saving={mergeSaving}
          onPreview={runMergePreview}
          onMerge={confirmMerge}
          onCancel={closeMerge}
        />
      )}

      {internalDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => setInternalDialog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[460px] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-gray-800">
              {internalDialog.action.delete
                ? "Delete internal-link rule"
                : "Disable internal-link rule"}
            </h2>
            <p className="text-sm text-gray-600">
              {internalDialog.affected === null
                ? "Counting affected links…"
                : `${internalDialog.affected.toLocaleString()} transaction${
                    internalDialog.affected === 1 ? "" : "s"
                  } are currently linked by this rule.`}
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {internalDialog.action.delete ? (
                <button
                  type="button"
                  onClick={() => confirmInternal(true)}
                  className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
                >
                  Delete & unlink all
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => confirmInternal(false)}
                    className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50"
                  >
                    Keep existing links
                  </button>
                  <button
                    type="button"
                    onClick={() => confirmInternal(true)}
                    className="px-3 py-1.5 text-sm rounded bg-orange-600 text-white hover:bg-orange-700"
                  >
                    Unlink all
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setInternalDialog(null)}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {txnsDialogStakeholder && (
        <StakeholderTxnsDialog
          stakeholder={txnsDialogStakeholder}
          perspectiveId={getPerspectiveId() ?? null}
          onClose={() => setTxnsDialogStakeholder(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
